// ==================== SERVICIO DE AUTENTICACIÓN ====================
// Maneja registro, login y gestión de sesión

import api from './api';

const authService = {
  // ==================== REGISTRO ====================
  // Crear una nueva cuenta de usuario
  async registro(email, password, registerName, registerManager) {
    try {
      const response = await api.post('/auth/registro', {
        email,
        password,
        registerName,
        registerManager,
      });

      // Guardar token y datos del usuario en localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('usuario', JSON.stringify(response.data.usuario));

        // Multi-proyecto: guardar array y proyecto activo
        if (response.data.proyectos) {
          localStorage.setItem('proyectos', JSON.stringify(response.data.proyectos));
        }
        const proyectoActivo = response.data.proyectoActivo;
        if (proyectoActivo) {
          const pid = proyectoActivo.id || proyectoActivo._id;
          localStorage.setItem('proyectoActivoId', pid);
          localStorage.setItem('proyectoId', pid); // backward compat
        }
      }

      return response.data;
    } catch (error) {
      console.error('❌ Error detallado en registro:', {
        mensaje: error.response?.data?.mensaje,
        error: error.response?.data?.error,
        detalles: error.response?.data?.detalles,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        errorCompleto: error
      });
      
      throw error.response?.data || { error: 'Error al registrar usuario', mensaje: error.message };
    }
  },

  // ==================== LOGIN ====================
  // Iniciar sesión con email y contraseña
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      // Guardar token y datos del usuario en localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('usuario', JSON.stringify(response.data.usuario));
        localStorage.setItem('userPwd', password);

        // Multi-proyecto: guardar array y proyecto activo
        if (response.data.proyectos) {
          localStorage.setItem('proyectos', JSON.stringify(response.data.proyectos));
        }
        const proyectoActivo = response.data.proyectoActivo;
        if (proyectoActivo) {
          const pid = proyectoActivo.id || proyectoActivo._id;
          localStorage.setItem('proyectoActivoId', pid);
          localStorage.setItem('proyectoId', pid); // backward compat
        }
      }

      return response.data;
    } catch (error) {
      console.error('❌ Error detallado en login:', {
        mensaje: error.response?.data?.mensaje,
        error: error.response?.data?.error,
        detalles: error.response?.data?.detalles,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        errorCompleto: error
      });
      
      throw error.response?.data || { error: 'Error al iniciar sesión', mensaje: error.message };
    }
  },

  // ==================== LOGOUT ====================
  // Cerrar sesión y limpiar datos locales
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    localStorage.removeItem('proyectoId');
    localStorage.removeItem('proyectos');
    localStorage.removeItem('proyectoActivoId');
    localStorage.removeItem('userPwd');
  },

  // ==================== OBTENER PERFIL ====================
  // Obtener información del usuario autenticado
  async obtenerPerfil() {
    try {
      const response = await api.get('/auth/perfil');
      
      // Actualizar datos del usuario en localStorage
      if (response.data.usuario) {
        localStorage.setItem('usuario', JSON.stringify(response.data.usuario));
      }

      // Multi-proyecto: actualizar lista y proyecto activo
      if (response.data.proyectos) {
        localStorage.setItem('proyectos', JSON.stringify(response.data.proyectos));
      }
      const proyectoActivo = response.data.proyectoActivo;
      if (proyectoActivo) {
        const pid = proyectoActivo.id || proyectoActivo._id;
        localStorage.setItem('proyectoActivoId', pid);
        localStorage.setItem('proyectoId', pid);
      }

      return response.data;
    } catch (error) {
      console.error('Error al obtener perfil:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al obtener perfil' };
    }
  },

  // ==================== ACTUALIZAR PERFIL ====================
  // Actualizar información del usuario
  async actualizarPerfil(datos) {
    const { nombre, avatar } = typeof datos === 'object' ? datos : { nombre: datos };
    try {
      const response = await api.put('/auth/perfil', {
        nombre,
        avatar,
      });
      
      // Actualizar datos del usuario en localStorage
      if (response.data.usuario) {
        localStorage.setItem('usuario', JSON.stringify(response.data.usuario));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar perfil:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al actualizar perfil' };
    }
  },

  // ==================== CAMBIAR CONTRASEÑA ====================
  // Cambiar la contraseña del usuario
  async cambiarPassword(passwordActual, passwordNuevo) {
    try {
      const response = await api.put('/auth/cambiar-password', {
        passwordActual,
        passwordNuevo,
      });
      
      // Actualizar la contraseña guardada en localStorage
      localStorage.setItem('userPwd', passwordNuevo);
      
      return response.data;
    } catch (error) {
      console.error('Error al cambiar contraseña:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al cambiar contraseña' };
    }
  },

  // ==================== CAMBIAR EMAIL ====================
  // Cambiar el email del usuario
  async cambiarEmail(emailNuevo) {
    try {
      const response = await api.put('/auth/cambiar-email', {
        emailNuevo,
      });
      
      // Actualizar datos del usuario en localStorage
      if (response.data.usuario) {
        localStorage.setItem('usuario', JSON.stringify(response.data.usuario));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al cambiar email:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al cambiar email' };
    }
  },

  // ==================== ACTUALIZAR PROYECTO ====================
  // Actualizar información del proyecto del usuario
  async actualizarProyecto(nombre, manager) {
    try {
      const response = await api.put('/auth/proyecto', {
        nombre,
        manager,
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar proyecto:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al actualizar proyecto' };
    }
  },

  // ==================== ACTUALIZAR DÍAS POR PROCESO ====================
  // Actualizar configuración de días por proceso del proyecto
  async actualizarDiasProcesos(diasProcesos) {
    try {
      const response = await api.put('/auth/proyecto/dias-procesos', {
        diasProcesos,
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar días por proceso:', error.response?.data || error.message);
      throw error;
    }
  },

  // ==================== ACTUALIZAR DÍAS POR PROCESO MATERIALES ====================
  // Actualizar configuración de días por proceso de materiales del proyecto
  async actualizarDiasProcesosMateriales(diasProcesosMateriales) {
    try {
      // Timeout extendido porque puede actualizar muchos materiales
      const response = await api.put('/auth/proyecto/dias-procesos-materiales', {
        diasProcesosMateriales,
      }, {
        timeout: 60000 // 60 segundos para esta operación
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar días por proceso de materiales:', error.response?.data || error.message);
      throw error;
    }
  },

  // ==================== OBTENER PROPIETARIO ====================
  // Obtener email del usuario propietario
  async obtenerPropietario() {
    try {
      const response = await api.get('/auth/propietario');
      return response.data;
    } catch (error) {
      console.error('Error al obtener propietario:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al obtener propietario', mensaje: error.message };
    }
  },

  // ==================== VERIFICAR SI ESTÁ AUTENTICADO ====================
  // Comprobar si el usuario tiene una sesión activa
  estaAutenticado() {
    const token = localStorage.getItem('token');
    const usuario = localStorage.getItem('usuario');
    return !!(token && usuario);
  },

  // ==================== OBTENER USUARIO ACTUAL ====================
  // Obtener datos del usuario desde localStorage
  obtenerUsuarioActual() {
    const usuarioStr = localStorage.getItem('usuario');
    return usuarioStr ? JSON.parse(usuarioStr) : null;
  },

  // ==================== OBTENER ID DEL PROYECTO ====================
  // Obtener el ID del proyecto activo (backward compat)
  obtenerProyectoId() {
    return localStorage.getItem('proyectoActivoId') || localStorage.getItem('proyectoId');
  },

  // ==================== OBTENER LISTA DE PROYECTOS ====================
  obtenerProyectos() {
    const proyectosStr = localStorage.getItem('proyectos');
    return proyectosStr ? JSON.parse(proyectosStr) : [];
  },

  // ==================== OBTENER ID DEL PROYECTO ACTIVO ====================
  obtenerProyectoActivoId() {
    return localStorage.getItem('proyectoActivoId');
  },

  // ==================== CAMBIAR PROYECTO ACTIVO ====================
  cambiarProyectoActivo(proyecto) {
    const pid = proyecto.id || proyecto._id;
    localStorage.setItem('proyectoActivoId', pid);
    localStorage.setItem('proyectoId', pid);
  },

  // ==================== CREAR NUEVO PROYECTO ====================
  async crearProyecto(nombre, manager, descripcion, aplicarPlantilla = true) {
    try {
      const response = await api.post('/proyectos', { nombre, manager, descripcion, aplicarPlantilla });
      return response.data;
    } catch (error) {
      console.error('Error al crear proyecto:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al crear proyecto' };
    }
  },

  // ==================== LISTAR PROYECTOS ====================
  async listarProyectos() {
    try {
      const response = await api.get('/proyectos');
      return response.data.proyectos || [];
    } catch (error) {
      console.error('Error al listar proyectos:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al listar proyectos' };
    }
  },

  // ==================== ELIMINAR PROYECTO ====================
  async eliminarProyecto(proyectoId) {
    try {
      const response = await api.delete(`/proyectos/${proyectoId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar proyecto:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al eliminar proyecto' };
    }
  },

  // ==================== GERENTES ====================
  async crearGerente(datos) {
    try {
      const response = await api.post('/auth/gerentes', datos);
      return response.data;
    } catch (error) {
      console.error('Error al crear gerente:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al crear gerente' };
    }
  },

  async listarGerentes() {
    try {
      const response = await api.get('/auth/gerentes');
      return response.data.gerentes || [];
    } catch (error) {
      console.error('Error al listar gerentes:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al listar gerentes' };
    }
  },

  async actualizarGerente(id, datos) {
    try {
      const response = await api.put(`/auth/gerentes/${id}`, datos);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar gerente:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al actualizar gerente' };
    }
  },

  async eliminarGerente(id) {
    try {
      const response = await api.delete(`/auth/gerentes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar gerente:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al eliminar gerente' };
    }
  },
};

export default authService;
